import React from 'react';
import { Phone, MapPin, Clock, Star, Heart, Sparkles, Users, Award } from 'lucide-react';

const PHONE_NUMBER = '0433 121 529';
const PHONE_LINK = 'tel:0433121529';
const ADDRESS = '371 Crawford Rd, Dianella WA 6059';
const BUSINESS_IMAGE = '/salon-image.jpg';

const openingHours = [
  { day: 'Monday', hours: '9am – 6pm' },
  { day: 'Tuesday', hours: '9am – 6pm' },
  { day: 'Wednesday', hours: '9am – 6pm' },
  { day: 'Thursday', hours: '9am – 7:30pm' },
  { day: 'Friday', hours: '9am – 3pm' },
  { day: 'Saturday', hours: 'Closed' },
  { day: 'Sunday', hours: 'Closed' },
];

const reviews = [
  {
    text: "Just visited Tina for the first time and couldn't be happier! She booked me in last minute, did a wonderful job, and made me feel so relaxed. Very professional — I'll definitely be back.",
    date: '2 weeks ago'
  },
  {
    text: "Tina did a fantastic job with my eyelash and eyebrow tint — I would highly recommend her services. She is such a lovely person and very passionate about her work. She clearly loves what she does and it shows.",
    date: '2 months ago'
  },
  {
    text: "I am so grateful to have found Tina! Best eyebrow tint and lash tint I've ever had. Tina is welcoming and friendly and is very passionate about beauty and holistic health. I would highly recommend her services!",
    date: '6 months ago'
  }
];

const services = [
  { title: 'Eyebrow Treatments', description: 'Expert shaping, tinting, and grooming for perfectly defined brows' },
  { title: 'Lash Services', description: 'Professional lash tinting and treatments for stunning, natural-looking lashes' },
  { title: 'Facial Treatments', description: 'Rejuvenating facials tailored to your unique skin needs' },
  { title: 'Waxing Services', description: 'Gentle and effective hair removal for smooth, beautiful skin' }
];

const whyChooseUs = [
  { icon: Award, title: 'Experienced Professionals', description: 'Skilled beauty therapists with years of expertise' },
  { icon: Heart, title: 'Quality Products', description: 'Only the finest professional-grade products used' },
  { icon: Sparkles, title: 'Relaxing Environment', description: 'A calm, welcoming space designed for your comfort' },
  { icon: MapPin, title: 'Convenient Location', description: 'Easily accessible in the heart of Dianella' }
];

// Primary CTA Button Component
const CTAButton = ({ children, className = '' }) => (
  <a
    href={PHONE_LINK}
    className={`cta-button ${className}`}
  >
    <Phone size={18} />
    <span>{children}</span>
  </a>
);

// Star Rating Component
const StarRating = () => (
  <div className="star-rating">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={18} fill="#333333" color="#333333" />
    ))}
    <span className="rating-text">5.0 · 37 Google Reviews</span>
  </div>
);

// Header Component
const Header = () => (
  <header className="header">
    <div className="container header-inner">
      <a href="/" className="logo">
        Dianella House of Beauty
      </a>
      <a href={PHONE_LINK} className="header-phone">
        <Phone size={18} />
        <span>{PHONE_NUMBER}</span>
      </a>
    </div>
  </header>
);

// Hero Section
const HeroSection = () => (
  <section className="hero-section">
    <div className="container hero-container">
      <div className="hero-content">
        <StarRating />
        <h1 className="hero-title">
          Reveal Your Natural Beauty
        </h1>
        <p className="hero-subtitle">
          Experience professional beauty treatments in a relaxing, welcoming environment. 
          Where quality care meets stunning results.
        </p>
        <div className="hero-cta-group">
          <CTAButton>Call to Book</CTAButton>
          <a href={PHONE_LINK} className="phone-display">
            {PHONE_NUMBER}
          </a>
        </div>
      </div>
      <div className="hero-image-wrapper">
        <img 
          src={BUSINESS_IMAGE} 
          alt="Dianella House of Beauty interior" 
          className="hero-image"
        />
      </div>
    </div>
  </section>
);

// Services Section
const ServicesSection = () => (
  <section className="services-section">
    <div className="container">
      <div className="section-header">
        <h2 className="section-title">Our Services</h2>
        <p className="section-subtitle">
          Personalised beauty treatments designed to enhance your natural features
        </p>
      </div>
      <div className="services-grid">
        {services.map((service, index) => (
          <div key={index} className="service-card">
            <h3 className="service-title">{service.title}</h3>
            <p className="service-description">{service.description}</p>
          </div>
        ))}
      </div>
      <div className="section-cta">
        <CTAButton>Call Now</CTAButton>
      </div>
    </div>
  </section>
);

// Why Choose Us Section
const WhyChooseSection = () => (
  <section className="why-section">
    <div className="container">
      <div className="section-header">
        <h2 className="section-title">Why Choose Us</h2>
        <p className="section-subtitle">
          Trusted by clients across Perth for exceptional beauty services
        </p>
      </div>
      <div className="why-grid">
        {whyChooseUs.map((item, index) => {
          const IconComponent = item.icon;
          return (
            <div key={index} className="why-card">
              <div className="why-icon">
                <IconComponent size={24} strokeWidth={1.5} />
              </div>
              <h3 className="why-title">{item.title}</h3>
              <p className="why-description">{item.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Reviews Section
const ReviewsSection = () => (
  <section className="reviews-section">
    <div className="container">
      <div className="section-header">
        <div className="reviews-badge">
          <Star size={20} fill="#333333" color="#333333" />
          <span>5.0 Rating on Google</span>
        </div>
        <h2 className="section-title">What Our Clients Say</h2>
      </div>
      <div className="reviews-grid">
        {reviews.map((review, index) => (
          <div key={index} className="review-card">
            <div className="review-stars">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} fill="#333333" color="#333333" />
              ))}
            </div>
            <p className="review-text">"{review.text}"</p>
            <span className="review-date">{review.date}</span>
          </div>
        ))}
      </div>
      <div className="section-cta">
        <CTAButton>Speak With Us</CTAButton>
      </div>
    </div>
  </section>
);

// Location Section
const LocationSection = () => (
  <section className="location-section">
    <div className="container">
      <div className="location-grid">
        <div className="location-info">
          <h2 className="section-title">Visit Us</h2>
          <div className="location-details">
            <div className="location-item">
              <MapPin size={20} strokeWidth={1.5} />
              <div>
                <h3>Address</h3>
                <p>{ADDRESS}</p>
              </div>
            </div>
            <div className="location-item">
              <Phone size={20} strokeWidth={1.5} />
              <div>
                <h3>Phone</h3>
                <a href={PHONE_LINK}>{PHONE_NUMBER}</a>
              </div>
            </div>
          </div>
        </div>
        <div className="hours-info">
          <div className="hours-header">
            <Clock size={20} strokeWidth={1.5} />
            <h3>Opening Hours</h3>
          </div>
          <ul className="hours-list">
            {openingHours.map((item, index) => (
              <li key={index} className={item.hours === 'Closed' ? 'closed' : ''}>
                <span className="day">{item.day}</span>
                <span className="hours">{item.hours}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="map-container">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3384.5!2d115.8684!3d-31.8933!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2a32b11e8f5f0001%3A0x1!2s371+Crawford+Rd%2C+Dianella+WA+6059!5e0!3m2!1sen!2sau!4v1704534000000!5m2!1sen!2sau"
          width="100%"
          height="300"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Dianella House of Beauty Location"
        />
      </div>
    </div>
  </section>
);

// Final CTA Section
const FinalCTASection = () => (
  <section className="final-cta-section">
    <div className="container final-cta-container">
      <h2 className="final-cta-title">Ready to Feel Beautiful?</h2>
      <p className="final-cta-subtitle">
        Book your appointment today. Our friendly team is ready to welcome you.
      </p>
      <CTAButton className="cta-large">Book by Phone</CTAButton>
      <a href={PHONE_LINK} className="final-phone">
        {PHONE_NUMBER}
      </a>
    </div>
  </section>
);

// Footer Component
const Footer = () => (
  <footer className="footer">
    <div className="container footer-container">
      <div className="footer-brand">
        <h3>Dianella House of Beauty</h3>
        <p>Professional beauty treatments in Perth</p>
      </div>
      <div className="footer-contact">
        <a href={PHONE_LINK} className="footer-phone">
          <Phone size={18} />
          <span>{PHONE_NUMBER}</span>
        </a>
        <p className="footer-address">{ADDRESS}</p>
      </div>
      <div className="footer-hours">
        <p>Mon – Wed: 9am – 6pm</p>
        <p>Thu: 9am – 7:30pm</p>
        <p>Fri: 9am – 3pm</p>
      </div>
    </div>
    <div className="footer-bottom">
      <p>© {new Date().getFullYear()} Dianella House of Beauty. All rights reserved.</p>
    </div>
  </footer>
);

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="landing-page">
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <WhyChooseSection />
        <ReviewsSection />
        <LocationSection />
        <FinalCTASection />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
